import { onBeforeUnmount, ref } from 'vue'

export type WebSocketStatus = 'idle' | 'connecting' | 'open' | 'closing' | 'closed' | 'error'

export interface UseWebSocketOptions {
  onOpen?: (ev: Event) => void
  onMessage?: (data: any, ev: MessageEvent) => void
  onError?: (ev: Event) => void
  onClose?: (ev: CloseEvent) => void
  autoReconnect?: boolean
  reconnectDelayMs?: number
  maxReconnectAttempts?: number // Maximum reconnect attempts (0 = unlimited, default: 10)
  reconnectBackoffMultiplier?: number // Exponential backoff multiplier (default: 1.5)
  parseJson?: boolean
  // Authentication
  token?: string // JWT token for authentication
  // Token transmission method: 'query' (default) or 'protocol' (Sec-WebSocket-Protocol header)
  // Note: Browser WebSocket API doesn't support custom headers like Authorization
  // Ping/Pong options
  pingIntervalMs?: number // Khoảng thời gian gửi ping (mặc định: 30000ms = 30s)
  pongTimeoutMs?: number // Thời gian chờ pong response (mặc định: 5000ms = 5s)
  pingMessage?: string // Nội dung message ping (mặc định: 'ping')
  pongMessage?: string // Nội dung message pong mong đợi (mặc định: 'pong')
}

export function useWebSocket(url: string, options: UseWebSocketOptions = {}) {
  const wsRef = ref<WebSocket | null>(null)
  const status = ref<WebSocketStatus>('idle')
  const lastMessage = ref<any>(null)
  const messages = ref<any[]>([])
  const error = ref<any>(null)

  const {
    onOpen,
    onMessage,
    onError,
    onClose,
    autoReconnect = true,
    reconnectDelayMs = 1500,
    maxReconnectAttempts = 5,
    reconnectBackoffMultiplier = 1.5,
    parseJson = true,
    token,
    pingIntervalMs = 60000,
    pongTimeoutMs = 20000,
    pingMessage = 'ping',
    pongMessage = 'pong',
  } = options

  let reconnectTimer: number | null = null
  let pingTimer: number | null = null
  let pongTimer: number | null = null
  let reconnectAttempts = 0
  let currentReconnectDelay = reconnectDelayMs

  function cleanupReconnectTimer() {
    if (reconnectTimer !== null) {
      window.clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
  }

  function resetReconnectState() {
    reconnectAttempts = 0
    currentReconnectDelay = reconnectDelayMs
  }

  function shouldReconnect(closeCode: number): boolean {
    // Don't reconnect for authentication/policy violations
    if (closeCode === 1008) {
      console.warn('[WebSocket] Authentication failed, not reconnecting')
      return false
    }

    // Don't reconnect for protocol/data errors
    if ([1002, 1003, 1007, 1009, 1010, 1015].includes(closeCode)) {
      console.warn(`[WebSocket] Permanent error (code: ${closeCode}), not reconnecting`)
      return false
    }

    // Check max reconnect attempts
    if (maxReconnectAttempts > 0 && reconnectAttempts >= maxReconnectAttempts) {
      console.warn(`[WebSocket] Max reconnect attempts (${maxReconnectAttempts}) reached, stopping`)
      return false
    }

    return true
  }

  function cleanupPingTimer() {
    if (pingTimer !== null) {
      window.clearInterval(pingTimer)
      pingTimer = null
    }
  }

  function cleanupPongTimer() {
    if (pongTimer !== null) {
      window.clearTimeout(pongTimer)
      pongTimer = null
    }
  }

  function startPingPong() {
    cleanupPingTimer()
    cleanupPongTimer()

    if (pingIntervalMs <= 0)
      return

    // Gửi ping định kỳ
    pingTimer = window.setInterval(() => {
      if (status.value === 'open' && wsRef.value) {
        send(pingMessage)

        // Đặt timeout để chờ pong
        cleanupPongTimer()
        pongTimer = window.setTimeout(() => {
          console.warn('Không nhận được pong, đóng kết nối để reconnect...')
          close(1000, 'pong timeout')
        }, pongTimeoutMs)
      }
    }, pingIntervalMs)
  }

  function stopPingPong() {
    cleanupPingTimer()
    cleanupPongTimer()
  }

  function buildWebSocketUrl(baseUrl: string, authToken?: string): string {
    if (!authToken)
      return baseUrl

    // Add token to query parameter (for 'query' or 'both' methods)
    try {
      const urlObj = new URL(baseUrl)
      urlObj.searchParams.set('token', authToken)
      return urlObj.toString()
    }
    catch {
      // If URL parsing fails (e.g., relative URL), append query parameter manually
      const separator = baseUrl.includes('?') ? '&' : '?'
      return `${baseUrl}${separator}token=${encodeURIComponent(authToken)}`
    }
  }

  function connect() {
    try {
      cleanupReconnectTimer()
      status.value = 'connecting'

      // Build URL with token if using query parameter method
      const wsUrl = buildWebSocketUrl(url, token)

      const socket = new WebSocket(wsUrl)

      wsRef.value = socket

      socket.onopen = (ev) => {
        status.value = 'open'
        // Reset reconnect state on successful connection
        resetReconnectState()
        onOpen?.(ev)
        // Bắt đầu ping/pong khi kết nối mở
        // startPingPong()
      }

      socket.onmessage = (ev: MessageEvent) => {
        let payload: any = ev.data
        if (parseJson) {
          try {
            payload = JSON.parse(ev.data as string)
          }
          catch (_) {
            // keep raw when JSON.parse fails
          }
        }

        // Xử lý ping/pong
        if (payload === pingMessage) {
          // Nhận ping từ server, gửi pong lại
          send(pongMessage)
          return
        }
        if (payload === pongMessage) {
          // Nhận pong từ server, hủy timeout
          cleanupPongTimer()
          return
        }

        lastMessage.value = payload
        messages.value.push(payload)
        onMessage?.(payload, ev)
      }

      socket.onerror = (ev: Event) => {
        status.value = 'error'
        error.value = ev
        onError?.(ev)
      }

      socket.onclose = (ev: CloseEvent) => {
        status.value = 'closed'
        stopPingPong()
        onClose?.(ev)
        wsRef.value = null

        // Check if we should reconnect
        if (autoReconnect && shouldReconnect(ev.code)) {
          reconnectAttempts++

          // Calculate delay with exponential backoff
          const delay = currentReconnectDelay
          currentReconnectDelay = Math.min(
            currentReconnectDelay * reconnectBackoffMultiplier,
            30000, // Max 30 seconds
          )

          console.log(
            `[WebSocket] Reconnecting (attempt ${reconnectAttempts}/${maxReconnectAttempts || '∞'}) `
            + `in ${delay}ms...`,
          )

          cleanupReconnectTimer()
          reconnectTimer = window.setTimeout(() => {
            connect()
          }, delay)
        }
        else if (autoReconnect) {
          console.warn(`[WebSocket] Not reconnecting (code: ${ev.code}, reason: ${ev.reason || 'unknown'})`)
        }
      }
    }
    catch (e) {
      status.value = 'error'
      error.value = e
      console.error('[WebSocket] Connection error:', e)

      // Only reconnect on connection errors if we haven't exceeded max attempts
      if (autoReconnect && shouldReconnect(0)) {
        reconnectAttempts++
        const delay = currentReconnectDelay
        currentReconnectDelay = Math.min(
          currentReconnectDelay * reconnectBackoffMultiplier,
          30000,
        )

        console.log(
          `[WebSocket] Reconnecting after error (attempt ${reconnectAttempts}/${maxReconnectAttempts || '∞'}) `
          + `in ${delay}ms...`,
        )

        cleanupReconnectTimer()
        reconnectTimer = window.setTimeout(() => {
          connect()
        }, delay)
      }
    }
    // console.log('status', status.value)
  }

  function send(data: any) {
    if (status.value !== 'open' || !wsRef.value)
      return false

    try {
      const payload = typeof data === 'string' ? data : JSON.stringify(data)
      wsRef.value.send(payload)
      return true
    }
    catch (_) {
      return false
    }
  }

  function close(code?: number, reason?: string) {
    cleanupReconnectTimer()
    stopPingPong()
    if (wsRef.value && status.value === 'open') {
      status.value = 'closing'
      wsRef.value.close(code, reason)
    }
  }

  onBeforeUnmount(() => {
    close(1000, 'component unmounted')
  })

  return {
    connect,
    close,
    send,
    status,
    lastMessage,
    messages,
    error,
  }
}
